import {effect, Injectable, Injector, runInInjectionContext, signal} from '@angular/core';
import {
  OriginalPapaya,
  OriginalPapayaImageParams,
  OriginalPapayaInstance,
  OriginalPapayaParams, OriginalPapayaScanView,
  OriginalPapayaSurfaceParams,
  OriginalPapayaSurfaces,
  OriginalPapayaViewerScanCoord,
  OriginalPapayaViewerSlice,
  propertyNameForStoringControllerInPapaya,
} from '../../types/dashboard-study-papaya-original.types';
import {
  placePriorityByDerivativeType,
  placePriorityByScanType,
} from '../../symbols/dashboard-study-papaya-slices-sort.symbols';
import {
  PapayaColorTable,
  RGBAColor,
  RGBAColorPart,
  RGBBlueColor,
  RGBGreenColor,
  RGBRedColor,
} from '../../../dashboard-papaya/symbols/dashboard-papaya-color-table.symbols';
import {PapayaRGBColor} from '../../../dashboard-papaya/symbols/dashboard-papaya-general.symbols';
import {
  DashboardPapayaImageFile,
  DashboardPapayaImageFileMetadata,
  DashboardPapayaSurfaceFile,
} from '../../types/dashboard-study-papaya.types';
import {DashboardSeriesDerivativeType, DashboardSeriesScanClass} from '../../../dashboard-new/types/dashboard.types';
import {getOriginalPapayaColorTableGrayscale} from '../../symbols/dashboard-study-papaya-grayscale-table.symbols';
import {DashboardViewRegionBodySegment} from '../../../dashboard-view-regions/types/dashboard-view-regions.types';

/**
 * Main papaya object.
 * Added by the custom Papaya library.
 * Supposed to be loaded dynamically using `rt-dynamic-scripts` service.
 */
declare var papaya: OriginalPapaya;

/**
 * Papaya containers are used to store the papaya instances.
 */
declare var papayaContainers: Array<OriginalPapayaInstance>;

export interface PapayaScanControllerColoringScheme {
  [labelId: number]: RGBAColor;
}

export interface PapayaScanControllerSinglePartScheme {
  [labelId: number]: RGBAColorPart;
}

@Injectable()
export class DashboardStudyPapayaControllerService {
  public readonly defaultPapayaParams: OriginalPapayaParams = {
    allowScroll: false,
    scaleToFit: false,
    orthogonalTall: false,
    enableUpdateOrthogonalOnResize: false,
    orthogonal: false,
    singleSliceFullWidth: true, // NOTE custom property to fix bug with papaya sizing when orthogonal === false
    orthogonalDynamic: false,
    kioskMode: false,
    fullScreenPadding: true,
    showDisplay: false,
    putMainImageOnTheRight: false,
    dragEventEmitter: this.dragEventCallback.bind(this),
    customMouseUpEvent: this.mouseUpCallback.bind(this),
    customMouseDownEvent: this.mouseDownCallback.bind(this),
    surfaceBackground: [1, 1, 1, 0],
    showCurrentValue: false,
    showCoordinates: true,
    showControls: false,
    showSurfacePlanes: false,
    show3DCrosshairs: true,
    surfaceZoomRatio: 1.15,
    ignoreCanvasOffsetForInscriptions: true,
  } as OriginalPapayaParams;

  /**
   * A function that resolves the `initialFilesRendered` promise.
   */
  public initialFilesRenderedResolve: () => void;

  /**
   * A promise that resolves when the initial files are rendered in the Papaya viewer.
   * Used to ensure that the viewer is ready before any operations are performed.
   */
  public initialFilesRendered = new Promise<void>(resolve => {
    this.initialFilesRenderedResolve = resolve;
  });

  public readonly scanControllerList = signal<PapayaScanController[]>([]);
  public readonly surfaceControllerList = signal<PapayaSurfaceController[]>([]);
  public readonly isDragging = signal(false);
  public readonly mouseDown = signal(false);

  /**
   * There might be a lot of surface controllers in the Papaya controller,
   * as every surface is represented by a separate controller.
   * To faster access the surface controller by its id use this map.
   */
  public readonly surfaceIdToIndexMap = new Map<PapayaSurfaceController['id'], number>();

  public currentPapayaInstance: OriginalPapayaInstance;

  /**
   * Indicates if the papaya instance has been initialized.
   */
  public isPapayaStarted = false;

  constructor(private injector: Injector) {}

  public initializeDefaultPapayaParams(): void {
    checkPapayaLibLoaded();

    // Check if the papaya params are already initialized.
    // We want to avoid calling this method multiple times as it's unclear how to handle differences in the current and default params.
    if (papaya.params) {
      throw new Error('Papaya params are already initialized.');
    }

    // Initialize the papaya params with default values.
    papaya.params = this.defaultPapayaParams;
  }

  public preloadPapayaScanImages(): void {
    checkPapayaLibLoaded();

    // Preload will take no effect if called after `startPapaya()`.
    if (this.currentPapayaInstance) {
      throw new Error('Papaya instance is already initialized. Use `loadImageToPapaya()` method on scan controller to add more images.');
    }

    // Initialize the file array if it's not already initialized.
    if (!Array.isArray(papaya.params.files) && this.scanControllerList()?.length) {
      papaya.params.files = [];
    }

    // Add params and store index link in the controller.
    for (const [index, image] of this.scanControllerList().entries()) {
      papaya.params[image.name] = scanControllerToImageParams(image);
      papaya.params.files.push(image.papayaImageFile);
      image.papayaSliceIndex.set(index);
    }

    // Set the loading complete callback to link controllers to the papaya instance.
    // As it can be overwritten in the `preloadPapayaSurfaceImages()` method, set sync both for surfaces and slices.
    papaya.params.loadingComplete = () => {
      this.linkScreenVolumesToControllers();
      this.syncScanControllerList();
      this.linkPapayaSurfacesToControllers();
      this.syncSurfaceControllerList();

      // Remove the files from the papaya params to avoid loading them again.
      this.currentPapayaInstance.updateContainerParams({
        files: [],
      } as OriginalPapayaParams);

      // Resolve the initial files rendered promise to indicate that the initial files are rendered.
      this.initialFilesRenderedResolve?.();
    };
  }

  /**
   * Preloads the papaya surface images by adding them to the papaya params.
   * Updates the controllers with the papaya surface index on the loading complete callback.
   * This method should be called before starting the papaya instance.
   */
  public async preloadPapayaSurfaceImages(): Promise<void> {
    checkPapayaLibLoaded();

    // Preload will take no effect if called after `startPapaya()`.
    if (this.currentPapayaInstance) {
      throw new Error(
        'Papaya instance is already initialized. Use `loadSurfaceToPapaya()` method on scan controller to add more surfaces.'
      );
    }

    // Initialize the binarySurface array if it's not already initialized.
    if (!Array.isArray(papaya.params.binarySurfaces) && this.surfaceControllerList()?.length) {
      papaya.params.binarySurfaces = [];
    }

    // Add params and store index link in the controller.
    for (const [index, surface] of this.surfaceControllerList().entries()) {
      papaya.params[surface.papayaSurfaceFile.name] = surfaceControllerToSurfaceParams(surface);
      papaya.params.binarySurfaces.push(surface.papayaSurfaceFile);
      surface.papayaSurfaceIndex.set(index);
    }

    // Set the loading complete callback to link controllers to the papaya instance.
    // As it can be overwritten in the `preloadPapayaScanImages()` method, set sync both for surfaces and slices.
    papaya.params.loadingComplete = () => {
      this.linkScreenVolumesToControllers();
      this.syncScanControllerList();
      this.linkPapayaSurfacesToControllers();
      this.syncSurfaceControllerList();

      // Remove the files from the papaya params to avoid loading them again.
      this.currentPapayaInstance.updateContainerParams({
        binarySurfaces: [],
      } as OriginalPapayaParams);

      // Resolve the initial files rendered promise to indicate that the initial files are rendered.
      this.initialFilesRenderedResolve?.();
    };
  }

  /**
   * Starts the papaya instance.
   */
  public startPapaya(): void {
    checkPapayaLibLoaded();

    // Avoid calling this method multiple times as it will lead to the errors inside the papaya library.
    if (this.isPapayaStarted) {
      throw new Error('Papaya instance is already started.');
    }

    // Disable auto scroll to prevent the window from scrolling to (0,0) when papaya is started.
    // We just never want it to be done by the papaya library, as it's called in setTimeout and provides no tools to control it.
    papaya.Container.disableAutoScroll = true;

    // Initialize the papaya instance.
    papaya.Container.startPapaya();

    // Update the papaya instance status.
    this.isPapayaStarted = true;

    // Get the first papaya instance.
    this.currentPapayaInstance = getFirstPapayaInstance();
  }

  public redrawPapayaViewer(force = true, skipUpdate = false): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Redraw the papaya viewer.
    this.currentPapayaInstance.viewer.drawViewer(force, skipUpdate);
  }

  /**
   * Custom sort papaya slices.
   *
   */
  public customSortPapayaSlices(): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Sort the papaya slices based on the derivative type and scan type.
    this.currentPapayaInstance.viewer.screenVolumes.sort((a, b) => {
      const aDerivativeType = a[propertyNameForStoringControllerInPapaya].derivativeType;
      const aScanType = a[propertyNameForStoringControllerInPapaya].scanClass;
      const aWeight = placePriorityByDerivativeType[aDerivativeType] + placePriorityByScanType[aScanType];

      const bDerivativeType = b[propertyNameForStoringControllerInPapaya].derivativeType;
      const bScanType = a[propertyNameForStoringControllerInPapaya].scanClass;
      const bWeight = placePriorityByDerivativeType[bDerivativeType] + placePriorityByScanType[bScanType];

      return aWeight - bWeight;
    });

    // Update indices for each loaded scan controller.
    for (const [index, slice] of this.currentPapayaInstance.viewer.screenVolumes.entries()) {
      slice[propertyNameForStoringControllerInPapaya].papayaSliceIndex.set(index);
    }
  }

  /**
   * Adds a new scan controller to the list.
   */
  public addNewScanController(
    papayaImageFile: DashboardPapayaImageFile,
    initialColoringScheme?: PapayaScanControllerColoringScheme
  ): PapayaScanController {
    // Check if the id is unique.
    const isIdUnique = this.scanControllerList().every(controller => controller.id !== papayaImageFile.metadata.id);

    // Throw an error if the id is not unique.
    if (!isIdUnique) {
      throw new Error(`A scan controller with the id "${papayaImageFile.metadata.id}" already exists.`);
    }

    // Check if the name is unique.
    const isNameUnique = this.scanControllerList().every(controller => controller.name !== papayaImageFile.metadata.name);

    // Throw an error if the name is not unique.
    // We cannot have two scan controllers with the same name as they are going to be loaded in the papaya by the same property name,
    // and the last one will override the previous one.
    if (!isNameUnique) {
      throw new Error(`A scan controller with the name "${papayaImageFile.metadata.name}" already exists.`);
    }

    // Initialize the scan controller with the provided name and file.
    const scanController = new PapayaScanController(this, papayaImageFile, initialColoringScheme);

    // Add the scan controller to the list.
    this.scanControllerList.set([...this.scanControllerList(), scanController]);

    // Return pointer to the scan controller for simple access.
    return scanController;
  }

  public linkScreenVolumesToControllers(): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    const scanControllers = this.scanControllerList();

    // Link the screen volumes to the controllers.
    for (const [index, slice] of this.currentPapayaInstance.viewer.screenVolumes.entries()) {
      if (!slice[propertyNameForStoringControllerInPapaya]) {
        const matchingController = scanControllers.find(controller => controller.papayaSliceIndex() === index);

        // We expect that all slices have the matching controller.
        if (!matchingController) {
          throw new Error(`No matching controller found for slice index ${index}.`);
        }

        slice[propertyNameForStoringControllerInPapaya] = matchingController;
      }
    }
  }

  public syncScanControllerList(): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Reset the papaya slice index and loaded state for all scan controllers.
    for (const scanController of this.scanControllerList()) {
      scanController.papayaSliceIndex.set(-1);
      scanController.isLoadedToPapaya.set(false);
    }

    // Update the papaya slice index and loaded state for each loaded slice.
    for (const [index, slice] of this.currentPapayaInstance.viewer.screenVolumes.entries()) {
      const controller = slice[propertyNameForStoringControllerInPapaya];

      controller.papayaSliceIndex.set(index);
      controller.isLoadedToPapaya.set(true);
    }
  }

  public linkPapayaSurfacesToControllers(): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    const surfaceControllers = this.surfaceControllerList();

    // Link the screen volumes to the controllers.
    for (const [index, surface] of this.currentPapayaInstance.viewer.surfaces.entries()) {
      if (!surface[propertyNameForStoringControllerInPapaya]) {
        const matchingController = surfaceControllers.find(controller => controller.papayaSurfaceIndex() === index);

        // We expect that all surfaces have the matching controller.
        if (!matchingController) {
          throw new Error(`No matching controller found for surface index ${index}.`);
        }

        surface[propertyNameForStoringControllerInPapaya] = matchingController;
      }
    }
  }

  public syncSurfaceControllerList(): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Reset the papaya surface index and loaded state for all surface controllers.
    for (const scanController of this.surfaceControllerList()) {
      scanController.papayaSurfaceIndex.set(-1);
      scanController.isLoadedToPapaya.set(false);
    }

    // Update the papaya surface index and loaded state for each loaded surface.
    for (const [index, surface] of this.currentPapayaInstance.viewer.surfaces.entries()) {
      const controller = surface[propertyNameForStoringControllerInPapaya];

      controller.papayaSurfaceIndex.set(index);
      controller.isLoadedToPapaya.set(true);
    }
  }

  /**
   * Adds a new surface controller to the list.
   * @param papayaSurfaceFile - Surface file to be added (.gii, .gii.gz).
   */
  public addNewSurfaceController(papayaSurfaceFile: DashboardPapayaSurfaceFile): PapayaSurfaceController {
    // Initialize the surface controller with the provided file.
    const surfaceController = new PapayaSurfaceController(this, papayaSurfaceFile);

    // Add the surface controller to the list.
    const extractedSurfaceControllerList = this.surfaceControllerList();
    this.surfaceControllerList.set([...extractedSurfaceControllerList, surfaceController]);

    // Set the surface controller index in the map for faster access.
    this.surfaceIdToIndexMap.set(papayaSurfaceFile.id, extractedSurfaceControllerList.length);

    // Return pointer to the surface controller for simple access.
    return surfaceController;
  }

  /**
   * Moves the 3D perspective to the specified point in the papaya viewer.
   */
  public move3DPerspectiveToPoint(x: number, y: number, z: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the surface view is available.
    if (!this.currentPapayaInstance.viewer?.surfaceView) {
      throw new Error('Surface view is not available. Make sure at least one surface is loaded.');
    }

    // Move the 3D perspective to the specified point.
    this.currentPapayaInstance.viewer.surfaceView.movePerspectiveToPoint([x, y, z]);

    // Recalculate the world coordinates for the main viewer.
    this.currentPapayaInstance.viewer.container.coordinateChanged(this.currentPapayaInstance.viewer);

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.redrawPapayaViewer();
    }
  }

  /**
   * Moves the scan perspective to the specified point in the papaya viewer.
   */
  public moveScanInspectPoint(x: number, y: number, z: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Update the current coordinates in the papaya viewer.
    this.currentPapayaInstance.viewer.currentCoord.x = x;
    this.currentPapayaInstance.viewer.currentCoord.y = y;
    this.currentPapayaInstance.viewer.currentCoord.z = z;

    // Recalculate the world coordinates for the main viewer.
    this.currentPapayaInstance.viewer.container.coordinateChanged(this.currentPapayaInstance.viewer);

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.redrawPapayaViewer();
    }
  }

  /**
   * Moves the scan perspective to the specified point in the papaya viewer.
   */
  public move3DViewInspectPoint(x: number, y: number, z: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the surface view is available.
    if (!this.currentPapayaInstance.viewer?.surfaceView) {
      throw new Error('Surface view is not available. Make sure at least one surface is loaded.');
    }

    // Update the current coordinates in the papaya viewer.
    this.currentPapayaInstance.viewer.surfaceView.currentCoord.x = x;
    this.currentPapayaInstance.viewer.surfaceView.currentCoord.y = y;
    this.currentPapayaInstance.viewer.surfaceView.currentCoord.z = z;

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.redrawPapayaViewer();
    }
  }

  /**
   * Sets the zoom level for the 3D perspective in the papaya viewer.
   */
  public set3DViewZoom(zoom: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the surface view is available.
    if (!this.currentPapayaInstance.viewer?.surfaceView) {
      throw new Error('Surface view is not available. Make sure at least one surface is loaded.');
    }

    // Set the zoom level for the 3D perspective.
    this.currentPapayaInstance.viewer.surfaceView.zoom = zoom;

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.redrawPapayaViewer();
    }
  }

  /**
   * Set the orthogonal view parameters.
   * @param isOrthogonal - Indicates whether to set the orthogonal view or not.
   * @param resize - Indicates whether to resize the papaya viewer after setting the parameters.
   */
  public setOrthogonalViewParam(isOrthogonal: boolean, resize = true): void {
    // Update the papaya params.
    this.currentPapayaInstance.updateContainerParams({
      singleSliceFullWidth: !isOrthogonal,
      orthogonal: isOrthogonal,
    } as OriginalPapayaParams);

    // Update the active container params.
    this.currentPapayaInstance.viewer.container.orthogonal = isOrthogonal;
    this.currentPapayaInstance.viewer.container.singleSliceFullWidth = !isOrthogonal;

    // Resize the papaya components if requested.
    if (resize) {
      this.currentPapayaInstance.viewer.container.resizeViewerComponents(true);
    }
  }

  /**
   * Loads the specified surface controls to the papaya instance.
   * All controls should be already initialized.
   * All controls will be loaded simultaneously; the promise will resolve when all controls are loaded.
   * @param controlIds - Array of surface control IDs to be loaded.
   */
  public async loadSurfaceControlsToPapaya(controlIds: PapayaSurfaceController['id'][]): Promise<void> {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Extract controls from the list by their IDs.
    const allSurfaceControls = this.surfaceControllerList();
    const surfaceControlsToRender = controlIds.map(id => {
      const idx = this.surfaceIdToIndexMap.get(id);
      if (idx === undefined) {
        throw new Error(`Surface control with id "${id}" is not found.`);
      }
      return allSurfaceControls[idx];
    });

    // Create a promise that resolves when all controls are loaded.
    return new Promise(resolve => {
      // Track pending controls that are not loaded yet.
      const pendingControls = new Set(surfaceControlsToRender.filter(c => !c.isLoadedToPapaya()));

      // If there are no pending controls, resolve immediately.
      if (pendingControls.size === 0) {
        resolve();
        return;
      }

      // For signal-based reactivity, we need to run the effect in the injection context.
      runInInjectionContext(this.injector, () => {
        const effectRef = effect(() => {
          // Check all pending controls.
          for (const control of pendingControls) {
            if (control.isLoadedToPapaya()) {
              pendingControls.delete(control);
            }
          }

          // If all are rendered, consider the function complete.
          if (pendingControls.size === 0) {
            // Update the indices for all loaded surfaces.
            for (const [index, surface] of this.currentPapayaInstance.viewer.surfaces.entries()) {
              const controller = surface[propertyNameForStoringControllerInPapaya];
              controller.papayaSurfaceIndex.set(index);
            }

            // Remove the effect to avoid memory leaks.
            effectRef.destroy();

            // Resolve the promise indicating all controls are loaded.
            resolve();
          }
        });
      });

      // Start the batch load after setting up the watcher.
      this.currentPapayaInstance.viewer.bunchSurfaceLoad(surfaceControlsToRender);
    });
  }

  /**
   * Sets the image dimensions for the surface view in the papaya instance.
   */
  public setSurfaceViewImageDimensions(sizes: OriginalPapayaViewerScanCoord): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Update the volume header with the new sizes.
    this.currentPapayaInstance.viewer.surfaceView.volume.header.imageDimensions = {
      xDim: sizes.x,
      yDim: sizes.y,
      zDim: sizes.z,
    };
  }

  /**
   * Sets the voxel dimensions for the surface view in the papaya instance.
   */
  public setSurfaceViewVoxelDimensions(voxel: OriginalPapayaViewerScanCoord): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Update the volume header with the new sizes.
    this.currentPapayaInstance.viewer.surfaceView.volume.header.voxelDimensions = {
      xSize: voxel.x,
      ySize: voxel.y,
      zSize: voxel.z,
    };
  }

  /**
   * Sets the origin for the surface view in the papaya instance.
   */
  public setSurfaceViewOrigin(origin: OriginalPapayaViewerScanCoord): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Update the volume header with the new sizes.
    this.currentPapayaInstance.viewer.surfaceView.volume.header.origin = {
      x: origin.x,
      y: origin.y,
      z: origin.z,
    };
  }

  /**
   * Resets the camera position in the surface view of the papaya instance.
   */
  public resetSurfaceCameraPosition(bodySegment: DashboardViewRegionBodySegment): void {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the surface view is available.
    if (!this.currentPapayaInstance.viewer?.surfaceView) {
      throw new Error('Surface view is not available. Make sure at least one surface is loaded.');
    }

    // Reset the camera position in the surface view.
    if (bodySegment === DashboardViewRegionBodySegment.Brain) {
      this.currentPapayaInstance.viewer.surfaceView.resetOrientation();
    } else if (bodySegment === DashboardViewRegionBodySegment.FullBody) {
      this.currentPapayaInstance.viewer.surfaceView.resetOrientationForBody();
    } else {
      throw new Error(`Unable to reset surface view: ${bodySegment}`);
    }
  }

  /**
   * Callback for the papaya drag event.
   * This method is called when the user starts or stops dragging the papaya viewer.
   * @param isDragging - Indicates whether the user is currently dragging the papaya viewer.
   */
  public dragEventCallback(isDragging: boolean): void {
    // Update the dragging state.
    this.isDragging.set(isDragging);
  }

  /**
   * Callback for the papaya mouse up event.
   */
  public mouseUpCallback(): void {
    // Update the dragging state.
    this.mouseDown.set(false);
  }

  /**
   * Callback for the papaya mouse down event.
   */
  public mouseDownCallback(): void {
    // Update the dragging state.
    this.mouseDown.set(true);
  }

  /**
   * Sets the zoom factor for the papaya viewer.
   * @param zoomFactor - The zoom factor to be set for the papaya viewer.
   */
  public setZoomFactor(zoomFactor: number): void {
    this.currentPapayaInstance.viewer.setZoomFactor(zoomFactor);
  }

  /**
   * Reorders the views in the papaya viewer to a specific position based on the slice direction.
   */
  public reorderViewsToSpecificPosition(sliceEnum: OriginalPapayaViewerSlice, redrawAfterReorder = false): void {
    this.currentPapayaInstance.viewer.reorderViewsToSpecificPosition(sliceEnum, redrawAfterReorder);
  }

  /**
   * Set a coordinate in the viewer to be the point of the zoom.
   */
  public setExactPanLocation(
    horizontalLoc: number,
    verticalLoc: number,
    deepLock: number,
    sliceDirection: OriginalPapayaViewerSlice
  ): void {
    this.currentPapayaInstance.viewer.setExactPanLocation(horizontalLoc, verticalLoc, deepLock, sliceDirection);
  }

  /**
   * Returns the papaya dimensions based on the current papaya instance.
   */
  public getPapayaDimensions(): [number, number, number] {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the volume is available in the papaya viewer.
    if (!this.currentPapayaInstance.viewer?.volume) {
      throw new Error('Volume is not available. Make sure at least one image is loaded.');
    }

    // Get the volume dimensions from the papaya viewer.
    const volume = this.currentPapayaInstance.viewer.volume;
    return [volume.getXDim() || 1, volume.getYDim() || 1, volume.getZDim() || 1];
  }

  /**
   * Returns the dimensions based on the currently selected slice.
   */
  public getDimensionsBasedOnCurrentSlice(): [number, number, number] {
    // Check if the papaya instance is present.
    if (!this.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Check if the main image is available in the papaya viewer.
    if (!this.currentPapayaInstance?.viewer?.mainImage) {
      throw new Error('Main image is not available. Make sure at least one image is loaded.');
    }

    // Get the current slice direction from the papaya instance.
    const selectedSlice = this.currentPapayaInstance.viewer.mainImage.sliceDirection || OriginalPapayaViewerSlice.Axial;

    // Get the papaya dimensions based on the selected slice.
    const [xDim, yDim, zDim] = this.getPapayaDimensions();

    // Return the dimensions based on the selected slice.
    switch (selectedSlice) {
      case OriginalPapayaViewerSlice.Axial:
        return [xDim, yDim, zDim];
      case OriginalPapayaViewerSlice.Coronal:
        return [xDim, zDim, yDim];
      case OriginalPapayaViewerSlice.Sagittal:
        return [yDim, zDim, xDim];
      default:
        throw new Error(`Unknown slice direction: ${selectedSlice}`);
    }
  }

  /**
   * Updates the surface volume sizes in the papaya instance.
   */
  public updateSurfaceVolumeSizes(): void {
    this.currentPapayaInstance.viewer.surfaceView.updateSizes();
  }

  /**
   * Fit the papaya viewer to the parent element.
   */
  public fitPapayaToTheParentElement() {
    papaya.Container.resizePapaya(null, true);
  }

  /**
   * Fit the papaya viewer to the parent element.
   */
  public drawMainSliceOnCustomCanvas(canvas: HTMLCanvasElement): void {
    const mainSlice = this.currentPapayaInstance.viewer.mainImage as OriginalPapayaScanView;
    this.currentPapayaInstance.viewer.drawScreenSliceOnCustomCanvas(mainSlice, canvas);
  }
}

export class PapayaScanController {
  public readonly id: number;

  public isLoadedToPapaya = signal(false);
  public papayaSliceIndex = signal(-1);

  constructor(
    public papayaViewController: DashboardStudyPapayaControllerService,
    public papayaImageFile: DashboardPapayaImageFile,
    public coloringScheme?: PapayaScanControllerColoringScheme
  ) {
    this.id = papayaImageFile.metadata.id;
  }

  get name(): string {
    return this.papayaImageFile.metadata.name;
  }

  get derivativeType(): DashboardSeriesDerivativeType {
    return this.papayaImageFile.metadata.derivativeType;
  }

  get scanClass(): DashboardSeriesScanClass {
    return this.papayaImageFile.metadata.scanClass;
  }

  get scanImageMetadata(): DashboardPapayaImageFileMetadata {
    return this.papayaImageFile.metadata;
  }

  public async loadImageToPapaya(): Promise<void> {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Avoid duplicate loading to prevent desynchronization of papaya images and its controls.
    if (this.isLoadedToPapaya()) {
      // Common reasons for the image to be not shown despite being loaded are order (another image is above), visibility, and opacity.
      throw new Error('This image is already loaded to the papaya instance.');
    }

    // Update the callback to set the loaded state once it's rendered.
    await new Promise<void>(resolve => {
      this.papayaViewController.currentPapayaInstance.setLoadingCompleteCallback(() => {
        const itemIndex = this.papayaViewController.currentPapayaInstance.viewer.screenVolumes.length - 1;
        this.papayaViewController.currentPapayaInstance.viewer.screenVolumes[itemIndex][propertyNameForStoringControllerInPapaya] = this;

        this.isLoadedToPapaya.set(true);
        this.papayaSliceIndex.set(itemIndex);
        resolve();
      });

      // Update the papaya params with the new image.
      this.papayaViewController.currentPapayaInstance.updateContainerParams({
        [this.name]: scanControllerToImageParams(this),
      });

      // Load the image to the papaya instance.
      this.papayaViewController.currentPapayaInstance.viewer.loadImage([this.papayaImageFile]);
    });
  }

  public removeSliceFromPapayaViewer(forceRedraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // The image should be loaded to the papaya instance.
    if (!this.isLoadedToPapaya()) {
      throw new Error('This image is not loaded to the papaya instance.');
    }

    // The slice index should be valid.
    if (
      this.papayaSliceIndex() < 0 ||
      this.papayaSliceIndex() >= this.papayaViewController.currentPapayaInstance.viewer.screenVolumes.length
    ) {
      throw new Error('Invalid slice index.');
    }

    // Remove the slice image from the papaya instance.
    this.papayaViewController.currentPapayaInstance.viewer.closeOverlay(this.papayaSliceIndex(), forceRedraw);
    this.isLoadedToPapaya.set(false);

    // Update other slice indices.
    for (const scanController of this.papayaViewController.scanControllerList()) {
      if (scanController.papayaSliceIndex() > this.papayaSliceIndex()) {
        scanController.papayaSliceIndex.set(scanController.papayaSliceIndex() - 1);
      }
    }

    // Set the slice index to -1 to indicate that the image is not loaded.
    this.papayaSliceIndex.set(-1);
  }

  /**
   * Set the filter values for the slice to show only the specified regions.
   * @param filterValues - Array of filter values to be set.
   * @param redraw - Indicates whether to redraw the papaya viewer after setting the filter values.
   */
  public setFilterValues(filterValues: number[], redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    const currentScreenVolume = this.papayaViewController.currentPapayaInstance.viewer.screenVolumes[this.papayaSliceIndex()];
    currentScreenVolume.screenFilterValues = filterValues;

    if (redraw) {
      this.papayaViewController.redrawPapayaViewer();
    }
  }

  /**
   * Updates the slice colors based on the provided coloring scheme.
   * @param coloringScheme - The coloring scheme to be applied to the slice.
   * @param redraw - Indicates whether to redraw the papaya viewer after updating the colors.
   */
  public updateSliceColors(coloringScheme: PapayaScanControllerColoringScheme, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Get the current screen volume.
    const currentScreenVolume = this.papayaViewController.currentPapayaInstance.viewer.screenVolumes[this.papayaSliceIndex()];

    // Update the color table with the new coloring scheme.
    currentScreenVolume.colorTable = new CustomPapayaColorTable(coloringScheme);

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.papayaViewController.redrawPapayaViewer();
    }
  }

  /**
   * Sets the maximum label for the slice.
   * @param labelId - The label ID to be set as the maximum for the slice.
   * @param redraw - Indicates whether to redraw the papaya viewer after setting the maximum label.
   */
  public setSliceMaxLabel(labelId: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Get the current screen volume.
    const currentScreenVolume = this.papayaViewController.currentPapayaInstance.viewer.screenVolumes[this.papayaSliceIndex()];

    // Set the max label for the slice.
    currentScreenVolume.screenMax = labelId;
    currentScreenVolume.imageMax = labelId;
    currentScreenVolume.originalScreenMax = labelId;

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.papayaViewController.redrawPapayaViewer();
    }
  }

  /**
   * Updates the opacity of the slice.
   * Will take effect only if the custom opacity on the color table is set to `null`.
   * @param opacity - The opacity value to be set for the slice (0 to 1).
   * @param redraw - Indicates whether to redraw the papaya viewer after updating the opacity.
   */
  public updateSliceOpacity(opacity: number, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Get the current screen volume.
    const currentScreenVolume = this.papayaViewController.currentPapayaInstance.viewer.screenVolumes[this.papayaSliceIndex()];

    // Update the opacity of the current screen volume.
    currentScreenVolume.alpha = opacity;

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.papayaViewController.redrawPapayaViewer();
    }
  }
}

export class PapayaSurfaceController {
  public isLoadedToPapaya = signal(false);
  public papayaSurfaceIndex = signal(-1);

  constructor(
    public papayaViewController: DashboardStudyPapayaControllerService,
    public papayaSurfaceFile: DashboardPapayaSurfaceFile,
    public initColor?: PapayaRGBColor,
    public initAlpha?: number
  ) {}

  get id(): DashboardPapayaSurfaceFile['id'] {
    return this.papayaSurfaceFile.id;
  }

  get name(): DashboardPapayaSurfaceFile['name'] {
    return this.papayaSurfaceFile.name;
  }

  /**
   * Loads the surface to the papaya instance.
   */
  public async loadSurfaceToPapaya(): Promise<void> {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Avoid duplicate loading to prevent desynchronization of papaya images and its controls.
    if (this.isLoadedToPapaya()) {
      // Common reasons for the image to be not shown despite being loaded are visibility and opacity.
      throw new Error('This surface is already loaded to the papaya instance.');
    }

    // Update the callback to set the loaded state once it's rendered.
    await new Promise<void>(resolve => {
      // Update the callback to set the loaded state once it's rendered.
      this.papayaViewController.currentPapayaInstance.setLoadingCompleteCallback(() => {
        const itemIndex = this.papayaViewController.currentPapayaInstance.viewer.surfaces.length - 1;
        this.papayaViewController.currentPapayaInstance.viewer.surfaces[itemIndex][propertyNameForStoringControllerInPapaya] = this;

        this.isLoadedToPapaya.set(true);
        this.papayaSurfaceIndex.set(itemIndex);
        resolve();
      });

      // Update the papaya params with the new image.
      this.papayaViewController.currentPapayaInstance.updateContainerParams({
        [this.name]: surfaceControllerToSurfaceParams(this),
      });

      // Load the image to the papaya instance.
      this.papayaViewController.currentPapayaInstance.viewer.loadSurface([this.papayaSurfaceFile]);
    });
  }

  /**
   * Removes the surface from the papaya viewer.
   * @param redraw - Indicates whether to redraw the papaya viewer after removing the surface.
   */
  public removeSurfaceFromPapayaViewer(redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // The surface should be loaded to the papaya instance.
    if (!this.isLoadedToPapaya()) {
      throw new Error('This surface is not loaded to the papaya instance.');
    }

    // The surface index should be valid.
    if (
      this.papayaSurfaceIndex() < 0 ||
      this.papayaSurfaceIndex() >= this.papayaViewController.currentPapayaInstance.viewer.surfaces.length
    ) {
      throw new Error('Invalid surface index.');
    }

    // Remove the surface from the papaya instance.
    this.papayaViewController.currentPapayaInstance.viewer.removeSurface(this.papayaSurfaceIndex(), redraw);
    this.isLoadedToPapaya.set(false);

    // Update other surface indices.
    for (const surfaceController of this.papayaViewController.surfaceControllerList()) {
      if (surfaceController.papayaSurfaceIndex() > this.papayaSurfaceIndex()) {
        surfaceController.papayaSurfaceIndex.set(surfaceController.papayaSurfaceIndex() - 1);
      }
    }

    // Set the index to -1 to indicate that the surface is not loaded.
    this.papayaSurfaceIndex.set(-1);
  }

  /**
   * Updates the papaya surface parameters.
   * Used to change the surface color, alpha, etc.
   * @param params - The parameters to be updated in the papaya surface.
   * @param redraw - Indicates whether to redraw the papaya viewer after updating the parameters.
   */
  public updatePapayaSurfaceParams(params: Partial<OriginalPapayaSurfaces>, redraw = true): void {
    // Check if the papaya instance is present.
    if (!this.papayaViewController.currentPapayaInstance) {
      throw new Error('Papaya instance is not found. Try calling startPapaya() first.');
    }

    // Get the papaya surface instance.
    const papayaSurface = this.papayaViewController.currentPapayaInstance.viewer.surfaces[this.papayaSurfaceIndex()];

    // Update the papaya surface params with the new values.
    for (const param in params) {
      if (Object.getOwnPropertyDescriptor(params, param)) {
        papayaSurface[param] = params[param];
      }
    }

    // Redraw the papaya viewer if needed.
    if (redraw) {
      this.papayaViewController.redrawPapayaViewer();
    }
  }
}

/**
 * Check if the papaya library is loaded.
 */
export const checkPapayaLibLoaded = (): void => {
  // Check if the papaya library is loaded.
  if (!papaya || !papaya.Container) {
    throw new Error('Papaya library is not loaded. Load it using `rt-dynamic-scripts` service.');
  }
};

/**
 * Get the first papaya instance from the papaya containers.
 * As we usually have only one papaya viewer on the page, we can just return the first one.
 */
export const getFirstPapayaInstance = (): OriginalPapayaInstance => {
  // Check if the papaya library is loaded.
  checkPapayaLibLoaded();

  // Check if the papaya containers are initialized.
  if (!Array.isArray(papayaContainers) || !papayaContainers.length) {
    throw new Error('Papaya containers are not initialized or empty. Try calling startPapaya() first.');
  }

  // Return the first papaya instance.
  return papayaContainers[0];
};

/**
 * Transform papaya scan controller to the papaya params.
 */
export const scanControllerToImageParams = (papayaScanController: PapayaScanController): OriginalPapayaImageParams => {
  // Base images should be grayscale.
  if (papayaScanController.derivativeType === DashboardSeriesDerivativeType.Base) {
    const grayscaleColorTable = getOriginalPapayaColorTableGrayscale();
    return {
      lut: grayscaleColorTable,
      metadata: papayaScanController.scanImageMetadata,
    };

    // Labeled images colors are based on the provided color scheme.
  } else if (papayaScanController.derivativeType === DashboardSeriesDerivativeType.Labeled) {
    return {
      lut: new CustomPapayaColorTable(papayaScanController.coloringScheme),
      metadata: papayaScanController.scanImageMetadata,
    };
  }

  // Image should always have a derivative type.
  throw new Error('Papaya scan controller is not a base or labeled image.');
};

/**
 * Transform papaya surface controller to the papaya params.
 */
export const surfaceControllerToSurfaceParams = (papayaSurfaceController: PapayaSurfaceController): OriginalPapayaSurfaceParams => {
  // Check if the alpha is valid.
  if (papayaSurfaceController.initAlpha < 0 || papayaSurfaceController.initAlpha > 1) {
    throw new Error('Papaya surface controller alpha value should be in range [0, 1].');
  }

  // Use the default color and opacity if they are not provided.
  return {
    alpha: papayaSurfaceController.initAlpha ?? 1,
    color: papayaSurfaceController.initColor ? papayaSurfaceController.initColor : [1, 1, 1],
  };
};

/**
 * Extended Color table for the papaya viewer to add the alpha channel support.
 */
export interface PapayaColorAndOpacityTable extends PapayaColorTable {
  hasCustomizedAlpha: true;

  /**
   * Called for each point of the papaya viewer.
   * Return value is number in range [0, 255] which represents the alpha of the point.
   *
   * @param imageVal - image value; always 1.
   * @param mriLabelId - region code of the MRI label in the view part.
   */
  lookupAlpha: (imageVal: number, mriLabelId: number) => RGBRedColor;
}

/**
 * Custom color table for the papaya viewer to support custom coloring schemes and alpha channel.
 */
export class CustomPapayaColorTable implements PapayaColorAndOpacityTable {
  /**
   * The default color part to apply in case the color is not found in the color scheme.
   * 255 is the white color.
   */
  public readonly defaultColorPartToApply = 255;

  /**
   * The default alpha part to apply in case the alpha is not found in the color scheme.
   */
  public readonly defaultAlphaPartToApply = 0.9;

  /**
   * Indicates that this color table has a customized alpha channel.
   */
  public readonly hasCustomizedAlpha = true;

  public readonly redColoringScheme: PapayaScanControllerSinglePartScheme;
  public readonly blueColoringScheme: PapayaScanControllerSinglePartScheme;
  public readonly greenColoringScheme: PapayaScanControllerSinglePartScheme;
  public readonly alphaScheme: PapayaScanControllerSinglePartScheme;

  constructor(public coloringScheme?: PapayaScanControllerColoringScheme) {
    this.redColoringScheme = {};
    this.blueColoringScheme = {};
    this.greenColoringScheme = {};
    this.alphaScheme = {};

    // Initialize the scheme for each label id.
    for (const labelId in this.coloringScheme) {
      if (Object.getOwnPropertyDescriptor(this.coloringScheme, labelId)) {
        const rgba = this.coloringScheme[labelId];
        this.redColoringScheme[labelId] = rgba[0];
        this.greenColoringScheme[labelId] = rgba[1];
        this.blueColoringScheme[labelId] = rgba[2];
        this.alphaScheme[labelId] = rgba[3];
      }
    }
  }

  /**
   * Do not remove!
   * The papaya uses this method.
   * This function is called for each point of the papaya viewer (255x255x255).
   * Keep it as simple as possible due to the performance reasons.
   * Return value is number in range [0, 255] which represents the red color of the point on RGB scale.
   *
   * @param _ - image value.
   * @param mriLabelRegionCode - region code of the MRI label in the brain part.
   */
  lookupRed(_: number, mriLabelRegionCode: number): RGBRedColor {
    return this.redColoringScheme?.[mriLabelRegionCode] ?? this.defaultColorPartToApply;
  }

  /**
   * Do not remove!
   * The papaya uses this method.
   * This function is called for each point of the papaya viewer (255x255x255).
   * Keep it as simple as possible due to the performance reasons.
   * Return value is number in range [0, 1] which represents the opacity of the point.
   *
   * @param _ - image value.
   * @param mriLabelRegionCode - region code of the MRI label in the brain part.
   */
  lookupAlpha(_: number, mriLabelRegionCode: number): RGBRedColor {
    return this.alphaScheme?.[mriLabelRegionCode] ?? this.defaultAlphaPartToApply;
  }

  /**
   * Do not remove!
   * The papaya uses this method.
   * This function is called for each point of the papaya viewer (255x255x255).
   * Keep it as simple as possible due to the performance reasons.
   * Return value is number in range [0, 255] which represents the green color of the point on RGB scale.
   *
   * @param _ - image value.
   * @param mriLabelRegionCode - region code of the MRI label in the brain part.
   */
  lookupGreen(_: number, mriLabelRegionCode: number): RGBGreenColor {
    return this.greenColoringScheme?.[mriLabelRegionCode] ?? this.defaultColorPartToApply;
  }

  /**
   * Do not remove!
   * The papaya uses this method.
   * This function is called for each point of the papaya viewer (255x255x255).
   * Keep it as simple as possible due to the performance reasons.
   * Return value is number in range [0, 255] which represents the blue color of the point on RGB scale.
   *
   * @param _ - image value.
   * @param mriLabelRegionCode - region code of the MRI label in the brain part.
   */
  lookupBlue(_: number, mriLabelRegionCode: number): RGBBlueColor {
    return this.blueColoringScheme?.[mriLabelRegionCode] ?? this.defaultColorPartToApply;
  }
}
